import clsx from 'clsx';
import React from 'react';
import { DialogConfirm, Icon } from 'ui';
import useSessionTimeout from './useSessionTimeout';
// Styles live in the global SCSS bundle (src/app/style/_index.scss). Colocated
// component SCSS imported via JS would not have design-system variables or
// typography classes in scope under the app's rsbuild sass setup.

/**
 * SessionTimeout — global session-expiry warning (WCAG 2.2.1).
 *
 * Pure JSX: every handler and derived value lives in `useSessionTimeout`.
 * Renders an accessible alert dialog with a live countdown. The visible number
 * is `aria-hidden` inside a `role="timer"` region (composed `aria-label`), while
 * an `aria-live` region (mounted with the open dialog) announces only milestone
 * crossings.
 */
function SessionTimeout(): React.JSX.Element {
  const { accessibleLabel, announcement, formattedTime, isCritical, isOpen, onContinue, onLogout, t } = useSessionTimeout();

  return (
    <DialogConfirm
      buttonTextCancel={t('auth:Log out')}
      buttonTextSuccess={t('auth:Continue session')}
      hasCancelButton
      iconName="warning"
      isOpen={isOpen}
      // Escape / backdrop dismiss is treated as "Log out": the session is
      // expiring, so the safest non-continue outcome is a clean teardown.
      onCancel={onLogout}
      onClose={onLogout}
      onSuccess={onContinue}
      text={t('auth:Your session is about to expire. Do you want to stay signed in?')}
      title={t('auth:Session about to expire')}
    >
      <div
        aria-label={accessibleLabel}
        className={clsx('session-timeout__countdown', { 'session-timeout__countdown--critical': isCritical })}
        role="timer"
      >
        {isCritical && <Icon ariaHidden color="inherit" name="alert" size="sm" />}
        <span aria-hidden="true" className="session-timeout__time">
          {formattedTime}
        </span>
      </div>
      {/* Live region (mounted while the dialog is open): text only changes at
          milestones — the countdown itself is not announced every second. */}
      <output aria-live="polite" className="visually-hidden">
        {announcement}
      </output>
    </DialogConfirm>
  );
}

export default SessionTimeout;
